import toast from "react-hot-toast";
import Close from "~icons/material-symbols/close";
import ContentCopy from "~icons/material-symbols/content-copy-outline";
import OpenInNew from "~icons/material-symbols/open-in-new";

import { css } from "../../../../styled-system/css";
import { IconImage } from "../../../components/icon-image";
import { t } from "../../../libs/i18n";
import { trimPrefecture } from "../../../libs/utils";
import type { StoreDetail } from "../types";

type StoreDetailModalProps = {
  isOpen: boolean;
  onClose: () => void;
  detail: StoreDetail | null;
};

export function StoreDetailModal({
  isOpen,
  onClose,
  detail,
}: StoreDetailModalProps) {
  if (!isOpen) return null;

  const { store } = detail ?? {};
  const availableFundSources = detail?.availableFundSources ?? [];

  if (!store) {
    return null;
  }

  const city = trimPrefecture(store.city ?? "");

  return (
    <div
      className={css({
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1000,
        animation: "slide-up 0.2s ease-out",
        overflowY: "auto",
      })}
    >
      <div
        className={css({
          mt: "16px",
          bg: "background.background",
          rounded: "xl",
          minHeight: "calc(100% - 16px)",
          borderTop: "1px solid",
          borderColor: "border.secondary",
        })}
      >
        <header
          className={css({
            h: "48px",
            justifyContent: "center",
            alignItems: "center",
            display: "flex",
            position: "sticky",
            top: 0,
            bg: "background.background",
            zIndex: 1,
            color: "text.primary",
            roundedTop: "xl",
          })}
        >
          <h1
            className={css({
              color: "text.primary",
              fontWeight: "500",
              fontSize: "18px",
            })}
          >
            {t("店舗詳細")}
          </h1>
          <button
            type="button"
            className={css({
              position: "absolute",
              right: 0,
              top: 0,
              height: "48px",
              width: "48px",
              pr: "16px",
              textAlign: "center",
            })}
            onClick={onClose}
          >
            <Close className={css({ width: "24px", height: "24px" })} />
          </button>
        </header>
        <div
          className={css({
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            pt: "16px",
            px: "16px",
            pb: "calc(env(safe-area-inset-bottom, 16px) + 128px)",
          })}
        >
          <div
            className={css({
              display: "flex",
              gap: "16px",
            })}
          >
            <div
              className={css({
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                bg: "white",
                rounded: "full",
                width: "48px",
                height: "48px",
                p: "0",
                flexShrink: 0,
              })}
            >
              <IconImage src={store.iconUrl} alt={store.name} size={48} />
            </div>

            <div>
              <h1
                className={css({
                  color: "text.primary",
                  fontSize: "20px",
                  fontWeight: 600,
                  wordBreak: "break-word",
                })}
              >
                {store.name}
              </h1>
              <div
                className={css({
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "8px",
                  color: "text.secondary",
                  fontSize: "14px",
                  fontWeight: 400,
                })}
              >
                {city ? (
                  <span
                    className={css({
                      flexShrink: 0,
                      rounded: "full",
                      border: "1px solid",
                      borderColor: "border.secondary",
                      bg: "surface.secondary",
                      color: "text.accentPrimary",
                      fontSize: "12px",
                      lineHeight: "16px",
                      px: "8px",
                      py: "1px",
                    })}
                  >
                    {city}
                  </span>
                ) : null}
                <span
                  className={css({
                    minWidth: 0,
                    lineHeight: "20px",
                    wordBreak: "break-word",
                  })}
                >
                  {store.category}
                </span>
              </div>
            </div>
          </div>

          <div
            className={css({
              bg: "white",
              px: "16px",
              rounded: "16px",
            })}
          >
            <div
              className={css({
                display: "flex",
                gap: "8px",
                py: "16px",
              })}
            >
              <h4
                className={css({
                  w: "80px",
                  color: "text.primary",
                  display: "flex",
                  alignItems: "center",
                  flexShrink: 0,
                })}
              >
                {t("住所")}
              </h4>
              <div
                className={css({
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                })}
              >
                <div
                  className={css({
                    display: "flex",
                    flexGrow: 1,
                  })}
                >
                  <p
                    className={css({
                      color: "text.secondary",
                    })}
                  >
                    {store.fullAddress}
                  </p>
                  <button
                    type="button"
                    className={css({
                      ml: "12px",
                    })}
                    onClick={() => {
                      navigator.clipboard.writeText(store.fullAddress ?? "");
                      toast.success(t("住所をコピーしました"));
                    }}
                  >
                    <ContentCopy
                      className={css({
                        w: "24px",
                        h: "24px",
                        color: "text.secondary",
                      })}
                    />
                  </button>
                </div>

                {store.location?.latitude && store.location?.longitude ? (
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${store.location?.latitude},${store.location?.longitude}`}
                    target="_blank"
                    rel="noreferrer"
                    className={css({
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      color: "text.accentPrimary",
                    })}
                  >
                    {t("Google マップ")}
                    <OpenInNew className={css({})} />
                  </a>
                ) : null}
              </div>
            </div>
            {availableFundSources.length > 0 && (
              <div
                className={css({
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  py: "16px",
                  borderTop: "1px solid",
                  borderColor: "border.primary",
                })}
              >
                <h4
                  className={css({
                    w: "80px",
                    color: "text.primary",
                    flexShrink: 0,
                  })}
                >
                  {t("使えるポイント")}
                </h4>
                <div
                  className={css({
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                  })}
                >
                  {availableFundSources.map((fundSource) => (
                    <span
                      key={fundSource.id}
                      className={css({
                        rounded: "full",
                        border: "2px solid",
                        borderColor: "border.accentPrimary",
                        bg: "surface.accentPrimaryLight",
                        color: "text.accentPrimary",
                        fontSize: "14px",
                        fontWeight: "600",
                        px: "12px",
                        py: "6px",
                      })}
                    >
                      {fundSource.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
